import React, { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Close from "@mui/icons-material/Close";

import { getToken } from "../../../../utils/common";
import { getApiConfigSafe } from "../../../../utils/api-config";
import { postV1AiFeatureSearchQuery } from "../../../../api-client/api.ai-feature/api";
import { DOC_TYPE_LABEL, MODULE_THUMB_COLOR, getSearchResultUrl } from "../../constants";
import { removeSeriesFromTitle } from "../../../../utils/format-text";

export function GlobalSearch() {
  const isAiEnabled = getApiConfigSafe()?.aiFeatureEnabled === 'true';
  if (!isAiEnabled) return null;
  const navigate = useNavigate();
  const [gsOpen, setGsOpen] = useState(false);
  const [gsQuery, setGsQuery] = useState("");
  const [gsApiResults, setGsApiResults] = useState<any>(null);
  const [gsLoading, setGsLoading] = useState(false);
  const [gsError, setGsError] = useState<string | null>(null);
  const gsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (gsDebounceRef.current) clearTimeout(gsDebounceRef.current);

    if (!gsQuery.trim()) {
      setGsApiResults(null);
      setGsError(null);
      setGsLoading(false);
      return;
    }

    setGsLoading(true);
    setGsError(null);
    gsDebounceRef.current = setTimeout(async () => {
      try {
        const token = getToken();
        const data = await postV1AiFeatureSearchQuery({ query: gsQuery, 'x-token': token['x-token'], 'x-timezone': String(token['x-timezone']) });
        setGsApiResults(data);
      } catch (err) {
        console.error('[SmartSearch] error:', err);
        setGsApiResults(null);
        setGsError('Search failed. Please try again.');
      } finally {
        setGsLoading(false);
      }
    }, 400);

    return () => {
      if (gsDebounceRef.current) clearTimeout(gsDebounceRef.current);
    };
  }, [gsQuery]);

  const gsGrouped = useMemo(() => {
    const results: any[] = gsApiResults?.data?.results ?? [];
    const map = new Map<string, any[]>();
    for (const r of results) {
      if (!map.has(r.doc_type)) map.set(r.doc_type, []);
      map.get(r.doc_type)!.push(r);
    }
    return map;
  }, [gsApiResults]);

  return (
    <div className={`gs${gsOpen ? " open" : ""}`}>
      <input
        className="gs-input"
        placeholder="Search employees, vendors, orders, anything…"
        value={gsQuery}
        onFocus={() => setGsOpen(true)}
        onBlur={() => setTimeout(() => setGsOpen(false), 150)}
        onChange={(e) => setGsQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setGsOpen(false);
            setGsQuery("");
          }
        }}
      />
      {gsOpen && gsQuery && (
        <button
          className="gs-clear"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setGsQuery("");
          }}
          aria-label="Clear search"
        >
          <Close style={{ fontSize: 14 }} />
        </button>
      )}

      {/**/}
      {/* {!gsOpen && <span className="gs-kbd">⌘K</span>} */}
      {/**/}

      {gsOpen && (
        <div className="gs-pop">
          {!gsQuery.trim() && (
            <div style={{ padding: "20px 16px", color: "#9CA3AF", fontSize: 13, textAlign: "center" }}>
              Start typing to search across all modules…
            </div>
          )}

          {gsQuery.trim() && gsLoading && (
            <div style={{ padding: "20px 16px", color: "#9CA3AF", fontSize: 13, textAlign: "center" }}>
              Searching…
            </div>
          )}

          {gsQuery.trim() && !gsLoading && gsError && (
            <div style={{ padding: "20px 16px", color: "#EF4444", fontSize: 13, textAlign: "center" }}>
              {gsError}
            </div>
          )}

          {gsQuery.trim() && !gsLoading && !gsError && gsGrouped.size === 0 && (
            <div style={{ padding: "20px 16px", color: "#9CA3AF", fontSize: 13, textAlign: "center" }}>
              No results for "{gsQuery}"
            </div>
          )}

          {gsQuery.trim() && !gsLoading && !gsError && [...gsGrouped.entries()].map(([docType, items]) => (
            <div key={docType} className="gs-group">
              <div className="gs-group-label">
                {DOC_TYPE_LABEL[docType] || docType.replace(/_/g, " ")}
                <span className="count"> · {items.length}</span>
              </div>
              {items.map((item: any) => {
                const highlightTitle = item.highlight?.title?.[0];
                const displayTitle = removeSeriesFromTitle(highlightTitle ?? item.title);
                const initials = item.title.split(" ").slice(0, 2).map((w: string) => w[0]?.toUpperCase() ?? "").join("");
                const bgColor = MODULE_THUMB_COLOR[item.module] ?? "linear-gradient(135deg,#6B7280,#4B5563)";
                const meta = [item.reference, item.date, item.amount != null ? Number(item.amount).toLocaleString(undefined, { maximumFractionDigits: 2 }) : null].filter(Boolean).join(" · ");
                const trail = item.status || item.approval_status;
                const url = getSearchResultUrl(item.doc_type, item.source_id, item);
                return (
                  <div
                    key={item.id}
                    className="gs-item"
                    style={{ cursor: url ? 'pointer' : 'default' }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      if (url) {
                        setGsOpen(false);
                        setGsQuery("");
                        navigate(url);
                      }
                    }}
                  >
                    <div className="gs-thumb av" style={{ background: bgColor }}>{initials}</div>
                    <div className="gs-body">
                      <div className="gs-title" dangerouslySetInnerHTML={{ __html: displayTitle }} />
                      {meta && <div className="gs-meta">{meta}</div>}
                    </div>
                    {trail && <span className="gs-trail">{trail}</span>}
                  </div>
                );
              })}
            </div>
          ))}

          {/* <div className="gs-foot"> */}
          {/*   <span><span className="gs-kbd">↑↓</span> Navigate</span> */}
          {/*   <span><span className="gs-kbd">↵</span> Open</span> */}
          {/*   <span><span className="gs-kbd">esc</span> Close</span> */}
          {/*   {gsApiResults?.data?.total > 0 && ( */}
          {/*     <span className="right">Show all {gsApiResults.data.total} results →</span> */}
          {/*   )} */}
          {/* </div> */}

        </div>
      )}
    </div>
  );
}

export default GlobalSearch;
